import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Download, FileSpreadsheet, History,
  Loader2, Play, RefreshCw, ShieldCheck, Sparkles, Upload,
} from "lucide-react";

const errMsg = (err, fallback) => formatApiError(err?.response?.data?.detail) || fallback;
const fmtDT = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

const LIST_TABS = [
  { id: "both_diff", list: "diffs", label: "Con differenze", color: "#F59E0B" },
  { id: "only_a", list: "only_a", label: "Solo in A", color: "#3B82F6" },
  { id: "only_b", list: "only_b", label: "Solo in B", color: "#8B5CF6" },
];

export default function ReconcileFiles() {
  const { user } = useAuth();
  const { features } = useFeatures();
  const [sid, setSid] = useState(null);
  const [files, setFiles] = useState({ a: null, b: null });
  const [uploading, setUploading] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [proposing, setProposing] = useState(false);
  const [keyA, setKeyA] = useState("");
  const [keyB, setKeyB] = useState("");
  const [norms, setNorms] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const proposedRef = useRef(false);

  const [historyFailed, setHistoryFailed] = useState(false);
  const loadHistory = useCallback(() => {
    api.get("/ai/tools/reconcile/sessions")
      .then(({ data }) => { setHistory(data); setHistoryFailed(false); })
      .catch(() => setHistoryFailed(true));
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const ensureSession = async () => {
    if (sid) return sid;
    const { data } = await api.post("/ai/tools/reconcile/sessions");
    setSid(data.id);
    return data.id;
  };

  const uploadFile = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const id = await ensureSession();
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ai/tools/reconcile/sessions/${id}/files/${slot}`, fd);
      setFiles((f) => ({ ...f, [slot]: data }));
      setProposal(null);
      setResult(null);
      proposedRef.current = false;
    } catch (err) {
      toast.error(errMsg(err, "Caricamento non riuscito"));
    } finally {
      setUploading(null);
    }
  };

  const reparse = async (slot, payload) => {
    setUploading(slot);
    try {
      const { data } = await api.post(`/ai/tools/reconcile/sessions/${sid}/files/${slot}/parse`, payload);
      setFiles((f) => ({ ...f, [slot]: data }));
      setProposal(null);
      setResult(null);
      proposedRef.current = false;
    } catch (err) {
      toast.error(errMsg(err, "Rilettura non riuscita"));
    } finally {
      setUploading(null);
    }
  };

  const propose = useCallback(async (id) => {
    setProposing(true);
    try {
      const { data } = await api.post(`/ai/tools/reconcile/sessions/${id}/propose`);
      setProposal(data);
      setKeyA(data.key_a);
      setKeyB(data.key_b);
      setNorms(data.normalizations || []);
    } catch (err) {
      toast.error(errMsg(err, "Proposta non disponibile"));
    } finally {
      setProposing(false);
    }
  }, []);

  useEffect(() => {
    if (sid && files.a && files.b && !proposedRef.current) {
      proposedRef.current = true;
      propose(sid);
    }
  }, [sid, files, propose]);

  const execute = async () => {
    setExecuting(true);
    try {
      const { data } = await api.post(`/ai/tools/reconcile/sessions/${sid}/execute`, {
        key_a: keyA, key_b: keyB, normalizations: norms, explain: true,
      });
      setResult(data);
      loadHistory();
    } catch (err) {
      toast.error(errMsg(err, "Esecuzione non riuscita"));
    } finally {
      setExecuting(false);
    }
  };

  const reset = () => {
    setSid(null); setFiles({ a: null, b: null }); setProposal(null);
    setResult(null); setKeyA(""); setKeyB(""); setNorms([]);
    proposedRef.current = false;
  };

  const openHistorySession = async (meta) => {
    try {
      const { data } = await api.get(`/ai/tools/reconcile/sessions/${meta.id}`);
      if (data.status !== "done") { toast.info("Sessione non completata"); return; }
      setSid(data.id);
      setFiles({ a: data.files.a, b: data.files.b });
      setProposal(data.proposal || null);
      setKeyA(data.mapping.key_a); setKeyB(data.mapping.key_b);
      setNorms(data.mapping.normalizations || []);
      setResult({ mapping: data.mapping, ...data.result });
      proposedRef.current = true;
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err, "Impossibile aprire la sessione"));
    }
  };

  if (user && user.role !== "super_admin" && features && !features.ai_tools) {
    return <Navigate to="/assistant" replace />;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="reconcile-page">
      <div>
        <Link to="/assistant" className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-3" data-testid="reconcile-back">
          <ArrowLeft size={15} /> Assistente AI
        </Link>
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2.5">
          <FileSpreadsheet size={22} className="text-accent-strong" /> Riconcilia due file
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Incrocia due elenchi Excel/CSV su una colonna chiave: chi manca di qua, chi manca di là, chi c'è in entrambi ma con dati diversi.
        </p>
        <p className="text-xs text-text-muted mt-2 flex items-center gap-1.5" data-testid="reconcile-privacy-note">
          <ShieldCheck size={13} className="text-success-strong" />
          All'AI viaggiano solo intestazioni, tipi e pochi campioni per colonna — mai i dati completi. Tutti i conteggi sono calcolati sui tuoi file, in modo deterministico.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FileSlot slot="a" label="File A (es. elenco del partner)" info={files.a} busy={uploading === "a"} onPick={(f) => uploadFile("a", f)} onReparse={(p) => reparse("a", p)} />
        <FileSlot slot="b" label="File B (es. export dal sistema)" info={files.b} busy={uploading === "b"} onPick={(f) => uploadFile("b", f)} onReparse={(p) => reparse("b", p)} />
      </div>

      {proposing && (
        <div className="bg-surface border border-border rounded-xl p-6 flex items-center gap-3 text-sm text-text-secondary" data-testid="reconcile-proposing">
          <Loader2 className="animate-spin" size={16} /> L'AI sta analizzando le strutture dei due file…
        </div>
      )}

      {proposal && files.a && files.b && (
        <div className="bg-surface border border-border rounded-xl p-6 space-y-4" data-testid="reconcile-proposal">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <Sparkles size={15} className="text-accent-strong" /> Proposta di mappatura
            </h2>
            <span className="text-xs px-2 py-0.5 rounded-md bg-accent-subtle text-accent-strong" data-testid="reconcile-confidence">
              confidenza {Math.round((proposal.confidence || 0) * 100)}%
            </span>
          </div>
          {proposal.rationale && <p className="text-sm text-text-secondary">{proposal.rationale}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <KeySelect label="Colonna chiave — File A" value={keyA} onChange={setKeyA}
                       options={files.a.columns.map((c) => c.name)} testId="reconcile-key-a" />
            <KeySelect label="Colonna chiave — File B" value={keyB} onChange={setKeyB}
                       options={files.b.columns.map((c) => c.name)} testId="reconcile-key-b" />
          </div>
          <div>
            <div className="text-xs font-medium text-text-secondary mb-2">Normalizzazioni</div>
            <div className="flex flex-wrap gap-2 mb-2.5">
              {["testo + zeri preservati", "trim spazi", "maiuscole ignorate", "«.0» rimosso"].map((t) => (
                <span key={t} className="text-[11px] px-2 py-1 rounded-md bg-subtle text-text-muted border border-border">{t} · sempre</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              <NormCheck id="zeros" label="Ignora zeri iniziali (es. POD letti come numero)" norms={norms} setNorms={setNorms} />
              <NormCheck id="spaces" label="Ignora tutti gli spazi (es. IBAN)" norms={norms} setNorms={setNorms} />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button type="button" onClick={execute} disabled={executing || !keyA || !keyB}
                    className="wl-btn wl-btn--primary" data-testid="reconcile-execute-btn">
              {executing ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Play size={14} className="mr-1.5" />}
              {executing ? "Elaborazione…" : "Esegui riconciliazione"}
            </button>
            <span className="text-xs text-text-muted">L'AI propone, tu decidi: controlla le colonne prima di eseguire.</span>
          </div>
        </div>
      )}

      {result && <ResultView sid={sid} result={result} files={files} onReset={reset} />}

      <HistoryList items={history} onOpen={openHistorySession} failed={historyFailed} onRetry={loadHistory} />
    </div>
  );
}

const FileSlot = ({ slot, label, info, busy, onPick, onReparse }) => {
  const inputRef = useRef(null);
  const [rowInput, setRowInput] = useState("");
  useEffect(() => { setRowInput(info ? String(info.header_row + 1) : ""); }, [info]);
  const multiSheet = (info?.sheets?.length || 0) > 1;
  const rowChanged = info && rowInput !== "" && Number(rowInput) !== info.header_row + 1;
  return (
    <div className="bg-surface border border-border rounded-xl p-5" data-testid={`reconcile-slot-${slot}`}>
      <div className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2">{label}</div>
      {info ? (
        <div>
          <div className="flex items-center gap-2 text-sm text-text-primary font-medium">
            <CheckCircle2 size={15} className="text-success-strong" /> {info.filename}
          </div>
          {multiSheet && (
            <div className="mt-2 flex items-center gap-2 text-xs text-text-secondary">
              <span>Foglio:</span>
              <select value={info.sheet || ""} disabled={busy}
                      onChange={(e) => onReparse({ sheet: e.target.value })}
                      className="wl-input" style={{ height: 28, fontSize: 12, width: "auto", maxWidth: 220 }}
                      data-testid={`reconcile-sheet-${slot}`}>
                {info.sheets.map((sh) => (
                  <option key={sh.name} value={sh.name}>{sh.name} ({sh.rows.toLocaleString("it-IT")} righe)</option>
                ))}
              </select>
            </div>
          )}
          <div className="text-xs text-text-secondary mt-1.5" data-testid={`reconcile-summary-${slot}`}>
            {info.rows.toLocaleString("it-IT")} righe · {info.columns.length} colonne: {info.columns.map((c) => c.name).join(", ")}
          </div>
          <div className="mt-2 text-xs text-text-secondary flex items-center gap-1.5 flex-wrap" data-testid={`reconcile-headerinfo-${slot}`}>
            <span>Intestazioni alla riga</span>
            <input type="number" min={1} value={rowInput} disabled={busy}
                   onChange={(e) => setRowInput(e.target.value)}
                   className="wl-input" style={{ width: 58, height: 26, fontSize: 12, padding: "0 8px" }}
                   data-testid={`reconcile-header-row-${slot}`} />
            {rowChanged ? (
              <button type="button" disabled={busy}
                      onClick={() => onReparse({ sheet: info.sheet, header_row: Number(rowInput) })}
                      className="wl-btn wl-btn--primary wl-btn--sm" style={{ height: 26 }}
                      data-testid={`reconcile-header-apply-${slot}`}>
                Rileggi
              </button>
            ) : (
              <span className="text-text-muted">— è corretto? Cambia il numero se serve.</span>
            )}
          </div>
          {info.dropped_unnamed > 0 && (
            <div className="mt-2 text-xs flex items-center gap-1.5 text-warning-strong" style={{ color: "var(--wl-warning-strong, #b45309)" }}
                 data-testid={`reconcile-dropped-${slot}`}>
              <AlertTriangle size={12} />
              {info.dropped_unnamed === 1
                ? "1 colonna senza nome è stata scartata (celle unite o vuote nell'intestazione)."
                : `${info.dropped_unnamed} colonne senza nome sono state scartate (celle unite o vuote nell'intestazione).`}
            </div>
          )}
          <button type="button" onClick={() => inputRef.current?.click()}
                  className="wl-btn wl-btn--ghost wl-btn--sm mt-3" data-testid={`reconcile-replace-${slot}`}>
            <RefreshCw size={13} className="mr-1.5" /> Sostituisci
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
                className="w-full border-2 border-dashed border-border rounded-lg py-8 flex flex-col items-center gap-2 text-text-secondary hover:border-accent hover:text-text-primary transition-colors"
                data-testid={`reconcile-upload-${slot}`}>
          {busy ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />}
          <span className="text-sm">{busy ? "Lettura in corso…" : "Carica .xlsx, .xls o .csv"}</span>
          <span className="text-[11px] text-text-muted">max 15 MB · 50.000 righe</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
             data-testid={`reconcile-input-${slot}`}
             onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
};

const KeySelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <div className="text-xs font-medium text-text-secondary mb-1.5">{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="wl-input w-full text-sm" data-testid={testId}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const NormCheck = ({ id, label, norms, setNorms }) => (
  <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
    <input type="checkbox" checked={norms.includes(id)} data-testid={`reconcile-norm-${id}`}
           onChange={(e) => setNorms((n) => e.target.checked ? [...n, id] : n.filter((x) => x !== id))}
           className="accent-current" />
    {label}
  </label>
);

const BigNum = ({ label, value, color, testId }) => (
  <div className="bg-surface border border-border rounded-xl p-5 text-center" data-testid={testId}>
    <div className="text-3xl font-semibold" style={{ color }}>{value.toLocaleString("it-IT")}</div>
    <div className="text-xs text-text-secondary mt-1">{label}</div>
  </div>
);

const ResultView = ({ sid, result, files, onReset }) => {
  const [tab, setTab] = useState("both_diff");
  const c = result.counts;

  const downloadReport = async () => {
    try {
      const { data } = await api.get(`/ai/tools/reconcile/sessions/${sid}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const el = document.createElement("a");
      el.href = url; el.download = "riconciliazione.xlsx"; el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  return (
    <div className="space-y-4" data-testid="reconcile-result">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-text-primary">Esito riconciliazione</h2>
        <div className="flex gap-2">
          <button type="button" onClick={downloadReport} className="wl-btn wl-btn--primary" data-testid="reconcile-report-btn">
            <Download size={14} className="mr-1.5" /> Report Excel
          </button>
          <button type="button" onClick={onReset} className="wl-btn wl-btn--ghost" data-testid="reconcile-new-btn">
            <RefreshCw size={14} className="mr-1.5" /> Nuova riconciliazione
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <BigNum label="Uguali in entrambi" value={c.both_same} color="#10B981" testId="count-both-same" />
        <BigNum label="In entrambi, con differenze" value={c.both_diff} color="#F59E0B" testId="count-both-diff" />
        <BigNum label="Solo in A" value={c.only_a} color="#3B82F6" testId="count-only-a" />
        <BigNum label="Solo in B" value={c.only_b} color="#8B5CF6" testId="count-only-b" />
      </div>

      {(c.dup_a > 0 || c.dup_b > 0 || c.empty_key_a > 0 || c.empty_key_b > 0) && (
        <div className="bg-warning-subtle border border-warning-strong/20 rounded-lg px-4 py-3 text-sm text-text-primary" data-testid="reconcile-warnings">
          {c.dup_a > 0 && <div>⚠ {c.dup_a} chiavi duplicate nel File A (usata la prima occorrenza, elenco nel report).</div>}
          {c.dup_b > 0 && <div>⚠ {c.dup_b} chiavi duplicate nel File B (usata la prima occorrenza, elenco nel report).</div>}
          {c.empty_key_a > 0 && <div>⚠ {c.empty_key_a} righe del File A con chiave vuota, escluse.</div>}
          {c.empty_key_b > 0 && <div>⚠ {c.empty_key_b} righe del File B con chiave vuota, escluse.</div>}
        </div>
      )}

      {result.explanation && (
        <div className="bg-accent-subtle border border-accent/25 rounded-lg px-4 py-3 text-sm text-text-primary" data-testid="reconcile-explanation">
          <span className="font-medium">In sintesi: </span>{result.explanation}
        </div>
      )}

      <div className="text-xs text-text-muted" data-testid="reconcile-normalizations">
        <b>Normalizzazioni applicate:</b> {result.normalizations_applied.join(" · ")}
        {result.compared_columns?.length > 0 && <> — <b>Colonne confrontate:</b> {result.compared_columns.join(", ")}</>}
        {(result.ignored_columns_a?.length > 0 || result.ignored_columns_b?.length > 0) && (
          <> — <b>Non confrontate</b> (nome diverso nei due file): {[...(result.ignored_columns_a || []), ...(result.ignored_columns_b || [])].join(", ")}</>
        )}
      </div>

      <div className="flex gap-1 border-b border-border">
        {LIST_TABS.map((t) => {
          const n = t.id === "both_diff" ? c.both_diff : c[t.id];
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`reconcile-tab-${t.id}`}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      tab === t.id ? "border-accent text-accent-strong" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
              {t.label} ({n})
            </button>
          );
        })}
      </div>

      <ListTable key={`${sid}-${tab}`} sid={sid} listName={LIST_TABS.find((t) => t.id === tab).list} pairs={result.pairs} />
    </div>
  );
};

const ListTable = ({ sid, listName, pairs }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [diffView, setDiffView] = useState("side");

  const load = useCallback(async (offset) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/tools/reconcile/sessions/${sid}/lists/${listName}`,
        { params: { offset, limit: 50 } });
      setTotal(data.total);
      setRows((r) => offset === 0 ? data.rows : [...r, ...data.rows]);
    } catch { toast.error("Impossibile caricare la lista"); }
    finally { setLoading(false); }
  }, [sid, listName]);
  useEffect(() => { load(0); }, [load]);

  if (loading && rows.length === 0) {
    return <div className="py-8 text-center text-text-muted"><Loader2 className="animate-spin mx-auto" size={16} /></div>;
  }
  if (total === 0) {
    return <div className="bg-surface border border-border rounded-xl p-8 text-center text-sm text-text-muted" data-testid="reconcile-list-empty">Nessuna riga in questa lista.</div>;
  }

  if (listName === "diffs") {
    const canSideBySide = pairs?.length > 0 && rows[0]?.row_a;
    const side = diffView === "side" && canSideBySide;
    return (
      <div className="bg-surface border border-border rounded-xl overflow-x-auto" data-testid="reconcile-list-diffs">
        {canSideBySide && (
          <div className="flex items-center gap-1 px-4 pt-3">
            {[["side", "Affiancato"], ["compact", "Solo differenze"]].map(([id, lbl]) => (
              <button key={id} type="button" onClick={() => setDiffView(id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        diffView === id ? "border-accent bg-accent-subtle font-medium text-text-primary" : "border-border text-text-secondary"}`}
                      data-testid={`reconcile-diff-view-${id}`}>
                {lbl}
              </button>
            ))}
          </div>
        )}
        {side ? (
          <table className="w-full text-sm min-w-[560px] mt-2" data-testid="reconcile-sidebyside">
            <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Chiave</th>
                <th className="text-left px-2 py-2.5 font-medium">File</th>
                {pairs.map(([ca, cb]) => (
                  <th key={ca} className="text-left px-3 py-2.5 font-medium">{ca === cb ? ca : `${ca} / ${cb}`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((d, i) => {
                const changed = new Set((d.fields || []).map((f) => f.col_a ?? f.column));
                const hl = (ca) => changed.has(ca) ? { background: "#FEF3C7", fontWeight: 500 } : undefined;
                return (
                  <React.Fragment key={i}>
                    <tr className="border-t-2 border-border">
                      <td rowSpan={2} className="px-4 py-2 font-medium text-text-primary align-top">{d.key_a || d.key}</td>
                      <td className="px-2 py-1.5 text-xs font-semibold text-text-muted">A</td>
                      {pairs.map(([ca]) => (
                        <td key={ca} className="px-3 py-1.5 text-text-primary whitespace-nowrap" style={hl(ca)}
                            data-testid={changed.has(ca) ? "diff-cell-a" : undefined}>
                          {(d.row_a || {})[ca] || <span className="text-text-muted">vuoto</span>}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-2 py-1.5 text-xs font-semibold text-text-muted">B</td>
                      {pairs.map(([ca, cb]) => (
                        <td key={cb} className="px-3 py-1.5 text-text-primary whitespace-nowrap" style={hl(ca)}
                            data-testid={changed.has(ca) ? "diff-cell-b" : undefined}>
                          {(d.row_b || {})[cb] || <span className="text-text-muted">vuoto</span>}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Chiave</th>
              <th className="text-left px-3 py-2.5 font-medium">Colonna</th>
              <th className="text-left px-3 py-2.5 font-medium">File A</th>
              <th className="text-left px-4 py-2.5 font-medium">File B</th>
            </tr>
          </thead>
          <tbody>
            {rows.flatMap((d, i) => d.fields.map((f, j) => (
              <tr key={`${i}-${j}`} className="border-t border-border">
                {j === 0 && <td className="px-4 py-2 font-medium text-text-primary" rowSpan={d.fields.length}>{d.key_a || d.key}</td>}
                <td className="px-3 py-2 text-text-secondary">{f.column}</td>
                <td className="px-3 py-2 text-text-primary">{f.a || <span className="text-text-muted">vuoto</span>}</td>
                <td className="px-4 py-2 text-text-primary">{f.b || <span className="text-text-muted">vuoto</span>}</td>
              </tr>
            )))}
          </tbody>
        </table>
        )}
        <LoadMore shown={rows.length} total={total} loading={loading} onMore={() => load(rows.length)} />
      </div>
    );
  }

  const cols = Object.keys(rows[0] || {});
  return (
    <div className="bg-surface border border-border rounded-xl overflow-x-auto" data-testid={`reconcile-list-${listName}`}>
      <table className="w-full text-sm min-w-[560px]">
        <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
          <tr>{cols.map((col) => <th key={col} className="text-left px-4 py-2.5 font-medium">{col}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {cols.map((col) => <td key={col} className="px-4 py-2 text-text-primary whitespace-nowrap">{r[col]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <LoadMore shown={rows.length} total={total} loading={loading} onMore={() => load(rows.length)} />
    </div>
  );
};

const LoadMore = ({ shown, total, loading, onMore }) => shown >= total ? null : (
  <div className="p-3 text-center border-t border-border">
    <button type="button" onClick={onMore} disabled={loading} className="wl-btn wl-btn--ghost wl-btn--sm" data-testid="reconcile-load-more">
      {loading ? <Loader2 className="animate-spin" size={13} /> : <ArrowRight size={13} className="mr-1.5" />}
      Mostra altre ({shown}/{total})
    </button>
  </div>
);

const HistoryList = ({ items, onOpen, failed, onRetry }) => (
  <div className="pt-2" data-testid="reconcile-history">
    <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
      <History size={15} /> Storico riconciliazioni
    </h2>
    {failed ? (
      <LoadErrorBox compact what="lo storico riconciliazioni" onRetry={onRetry} testId="reconcile-history-error" />
    ) : items.length === 0 ? (
      <div className="text-sm text-text-muted">Nessuna riconciliazione ancora.</div>
    ) : (
      <div className="bg-surface border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Data</th>
              <th className="text-left px-3 py-2.5 font-medium">File</th>
              <th className="text-left px-3 py-2.5 font-medium">Chiave</th>
              <th className="text-left px-3 py-2.5 font-medium">Esito</th>
              <th className="text-left px-4 py-2.5 font-medium">Utente</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className={`border-t border-border ${s.status === "done" ? "cursor-pointer hover:bg-subtle" : "opacity-60"}`}
                  onClick={() => s.status === "done" && onOpen(s)} data-testid={`reconcile-history-row-${s.id}`}>
                <td className="px-4 py-2.5 text-xs text-text-secondary">{fmtDT(s.created_at)}</td>
                <td className="px-3 py-2.5 text-text-primary">{s.file_a || "—"} ↔ {s.file_b || "—"}</td>
                <td className="px-3 py-2.5 text-text-secondary text-xs">{s.mapping ? `${s.mapping.key_a} ↔ ${s.mapping.key_b}` : "—"}</td>
                <td className="px-3 py-2.5 text-xs">
                  {s.counts ? (
                    <span className="text-text-secondary">
                      <b className="text-success-strong">{s.counts.both_same}</b> uguali · <b style={{ color: "#F59E0B" }}>{s.counts.both_diff}</b> diff · <b style={{ color: "#3B82F6" }}>{s.counts.only_a}</b> solo A · <b style={{ color: "#8B5CF6" }}>{s.counts.only_b}</b> solo B
                    </span>
                  ) : <span className="text-text-muted">bozza</span>}
                </td>
                <td className="px-4 py-2.5 text-text-secondary text-xs">{s.user_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);
